import {
  DialogId,
  DialogState,
  DialogStack,
  DialogGlobalConfig,
  DEFAULT_DIALOG_GLOBAL_CONFIG
} from './DialogProvider.types';

/**
 * Calculates the z-index for a dialog based on its position in the stack
 */
export const calculateZIndex = (
  stackPosition: number,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): number => {
  return globalConfig.baseZIndex + (stackPosition * globalConfig.zIndexIncrement);
};

/**
 * Gets the next available z-index for a new dialog
 */
export const getNextZIndex = (
  dialogs: Map<DialogId, DialogState>,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): number => {
  const openDialogs = Array.from(dialogs.values()).filter(dialog => dialog.isOpen);
  const stackDepth = openDialogs.length;
  return calculateZIndex(stackDepth, globalConfig);
};

/**
 * Creates a new dialog stack from the current dialogs
 */
export const createDialogStack = (
  dialogs: Map<DialogId, DialogState>,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): DialogStack => {
  // Get all open dialogs sorted by openedAt timestamp
  const openDialogs = Array.from(dialogs.values())
    .filter(dialog => dialog.isOpen)
    .sort((a, b) => a.openedAt - b.openedAt);

  const order = openDialogs.map(dialog => dialog.id);
  const activeId = order.length > 0 ? order[order.length - 1] : null;
  const count = order.length;
  const nextZIndex = calculateZIndex(count, globalConfig);

  return {
    order,
    activeId,
    count,
    nextZIndex,
  };
};

/**
 * Finds the position of a dialog in the stack
 */
export const findDialogStackPosition = (
  dialogId: DialogId,
  stack: DialogStack
): number => {
  return stack.order.indexOf(dialogId);
};

/**
 * Checks if a dialog is at the top of the stack
 */
export const isTopDialog = (dialogId: DialogId, stack: DialogStack): boolean => {
  return stack.activeId === dialogId;
};

/**
 * Gets all child dialogs for a given parent dialog
 */
export const getChildDialogs = (
  parentId: DialogId,
  dialogs: Map<DialogId, DialogState>
): DialogState[] => {
  return Array.from(dialogs.values()).filter(dialog =>
    dialog.parentId === parentId && dialog.isOpen
  );
};

/**
 * Gets all ancestor dialogs for a given dialog
 */
export const getAncestorDialogs = (
  dialogId: DialogId,
  dialogs: Map<DialogId, DialogState>
): DialogState[] => {
  const ancestors: DialogState[] = [];
  let currentDialog = dialogs.get(dialogId);

  while (currentDialog?.parentId) {
    const parent = dialogs.get(currentDialog.parentId);
    if (parent) {
      ancestors.unshift(parent);
      currentDialog = parent;
    } else {
      break;
    }
  }

  return ancestors;
};

/**
 * Validates if a dialog can be opened based on stack limits
 */
export const canOpenDialog = (
  dialogs: Map<DialogId, DialogState>,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): boolean => {
  const openCount = Array.from(dialogs.values()).filter(d => d.isOpen).length;
  return openCount < globalConfig.maxStackDepth;
};

/**
 * Updates z-indices for all dialogs in the stack
 */
export const updateAllZIndices = (
  dialogs: Map<DialogId, DialogState>,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): Map<DialogId, DialogState> => {
  const updatedDialogs = new Map(dialogs);
  const openDialogs = Array.from(dialogs.values())
    .filter(dialog => dialog.isOpen)
    .sort((a, b) => a.openedAt - b.openedAt);

  openDialogs.forEach((dialog, index) => {
    const newZIndex = calculateZIndex(index, globalConfig);
    if (dialog.zIndex !== newZIndex) {
      const updatedDialog: DialogState = {
        ...dialog,
        zIndex: newZIndex,
      };
      updatedDialogs.set(dialog.id, updatedDialog);

      // Call the z-index change callback if provided
      dialog.config.onZIndexChange?.(newZIndex);
    }
  });

  return updatedDialogs;
};

/**
 * Closes all child dialogs when a parent dialog closes
 */
export const closeChildDialogs = (
  parentId: DialogId,
  dialogs: Map<DialogId, DialogState>,
  reason: string = 'parent-closed'
): DialogId[] => {
  const closedIds: DialogId[] = [];
  const childDialogs = getChildDialogs(parentId, dialogs);

  childDialogs.forEach(child => {
    // Recursively close grandchildren first
    const grandchildrenIds = closeChildDialogs(child.id, dialogs, reason);
    closedIds.push(...grandchildrenIds);

    // Close this child dialog
    if (!child.config.persistOnParentClose) {
      closedIds.push(child.id);
      child.config.onClose?.(reason);
    }
  });

  return closedIds;
};

/**
 * Finds the appropriate dialog to focus after closing another dialog
 */
export const findFocusTargetAfterClose = (
  closedDialogId: DialogId,
  dialogs: Map<DialogId, DialogState>,
  stack: DialogStack
): DialogId | null => {
  // If there are still dialogs in the stack, focus the new top dialog
  if (stack.count > 0 && stack.activeId !== closedDialogId) {
    return stack.activeId;
  }

  // Find the parent dialog if it exists and is still open
  const closedDialog = dialogs.get(closedDialogId);
  if (closedDialog?.parentId) {
    const parent = dialogs.get(closedDialog.parentId);
    if (parent?.isOpen) {
      return parent.id;
    }
  }

  return null;
};

/**
 * Restores focus to the appropriate element after dialog closes
 */
export const restoreFocus = (
  dialogState: DialogState,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): void => {
  if (!globalConfig.restoreFocus) {
    return;
  }

  // Try to restore focus to the previous element
  if (dialogState.previousFocusElement) {
    try {
      dialogState.previousFocusElement.focus();
    } catch (error) {
      // Element might have been removed from DOM
      console.warn('Could not restore focus to previous element:', error);
    }
  }
};

/**
 * Validates dialog configuration
 */
export const validateDialogConfig = (
  config: Partial<DialogState['config']> | undefined
): boolean => {
  // Return true if config is undefined (no config to validate)
  if (!config) {
    return true;
  }

  // Check for valid z-index if provided
  if (config.zIndex !== undefined && config.zIndex < 0) {
    console.warn('Dialog z-index cannot be negative');
    return false;
  }

  // Check for valid mode
  if (config.mode && !['modal', 'embedded'].includes(config.mode)) {
    console.warn('Invalid dialog mode:', config.mode);
    return false;
  }

  // Check for valid variant
  if (config.variant && !['default', 'fullscreen', 'compact'].includes(config.variant)) {
    console.warn('Invalid dialog variant:', config.variant);
    return false;
  }

  return true;
};

/**
 * Creates a unique dialog ID if not provided
 */
export const generateDialogId = (prefix: string = 'dialog'): DialogId => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${prefix}-${timestamp}-${random}`;
};

/**
 * Deep clones a dialog state object
 */
export const cloneDialogState = (dialogState: DialogState): DialogState => {
  return {
    ...dialogState,
    config: { ...dialogState.config },
    childIds: [...dialogState.childIds],
  };
};

/**
 * Safely gets the currently focused element
 */
export const getCurrentFocusElement = (): HTMLElement | null => {
  try {
    return document.activeElement as HTMLElement;
  } catch (error) {
    console.warn('Could not get current focus element:', error);
    return null;
  }
};

/**
 * Utility to manage ESC key handling through the dialog stack
 */
export const handleEscapeKeyForStack = (
  stack: DialogStack,
  dialogs: Map<DialogId, DialogState>,
  globalConfig: DialogGlobalConfig = DEFAULT_DIALOG_GLOBAL_CONFIG
): DialogId[] => {
  const dialogsToClose: DialogId[] = [];

  if (globalConfig.escapeClosesAll) {
    // Close all dialogs in reverse order (top to bottom)
    dialogsToClose.push(...stack.order.slice().reverse());
  } else if (stack.activeId) {
    // Only close the top dialog if ESC is not disabled for it
    const topDialog = dialogs.get(stack.activeId);
    if (topDialog && !topDialog.config.disableEscapeKeyDown) {
      dialogsToClose.push(stack.activeId);
    }
  }

  return dialogsToClose;
};

/**
 * Debug utility to log current dialog stack state
 */
export const debugDialogStack = (
  dialogs: Map<DialogId, DialogState>,
  stack: DialogStack
): void => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  console.group('Dialog Stack Debug');
  console.log('Stack order:', stack.order);
  console.log('Active dialog:', stack.activeId);
  console.log('Dialog count:', stack.count);
  console.log('Next z-index:', stack.nextZIndex);

  console.log('Dialog details:');
  stack.order.forEach((id, index) => {
    const dialog = dialogs.get(id);
    if (dialog) {
      console.log(`  [${index}] ${id}:`, {
        zIndex: dialog.zIndex,
        mode: dialog.config.mode,
        variant: dialog.config.variant,
        parentId: dialog.parentId,
        openedAt: new Date(dialog.openedAt).toISOString(),
      });
    }
  });

  console.groupEnd();
};